import React, { useEffect, useCallback } from 'react';
import NewUserModal from '../UserOnboarding/NewUserModal';
import SpaceFormModal from '../HomePage/OverviewPage/SpacesStructure/SpaceFormModal';
import FloorFormModal from '../HomePage/OverviewPage/SpacesStructure/FloorFormModal';
import BOQFormModal from '../BOQ/BOQFormModal';
import BOQTemplateModal from '../BOQ/Setup/BOQTemplateModal';
import WindowFormModal from '../Spaces/Sections/Windows/WindowFormModal';
import DocumentsModal from '../../components/HomePage/BudgetPageNew/DocumentsModal';
import VendorFormModal from '../Vendors/VendorFormModal';
import PaymentFormModal from '../HomePage/BudgetPageNew/PaymentFormModal';
import QuoteFormModal from '../Vendors/QuoteFormModal';
import QuoteComparisonModal from '../Vendors/QuoteComparisonModal';
import TradeQuotesModal from '../Vendors/TradeQuotesModal';
import FloorPlanAnalysisWizard from '../FloorPlan/FloorPlanAnalysisWizard';
import ImportNotesModal from '../BOQ/ImportNotesModal';
import { useApp } from '../../contexts/AppContext';
import { MODAL_TYPES } from '../../constants/modalTypes';
import modalStyles from '../../styles/modals.module.css';

import './modal.css';

export default function ModalManager() {
  const { modalState, closeModal } = useApp();

  // Reference to the current modal's submit function
  let currentSubmitRef = null;

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        // Don't trigger on textarea shift+enter or IME composition
        if (e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        currentSubmitRef?.();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    if (modalState.isOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [modalState.isOpen, handleKeyPress]);

  if (!modalState.isOpen) return null;

  const renderModal = () => {
    switch (modalState.type) {
      case MODAL_TYPES.NEW_USER:
        return <NewUserModal onClose={closeModal} onSubmitRef={fn => (currentSubmitRef = fn)} />;
      case MODAL_TYPES.SPACE_FORM:
        return (
          <SpaceFormModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            floorId={modalState.data?.floorId}
            allFloors={modalState.data?.allFloors}
            getFloorName={modalState.data?.getFloorName}
            space={modalState.data?.space}
          />
        );
      case MODAL_TYPES.FLOOR_FORM:
        return (
          <FloorFormModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            floor={modalState.data?.floor}
            allFloors={modalState.data?.allFloors}
            data={modalState.data}
          />
        );
      case MODAL_TYPES.BOQ_FORM:
        return (
          <BOQFormModal
            open={modalState.isOpen}
            item={modalState.data?.item}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            bbChapters={modalState.data?.bbChapters}
            costTypes={modalState.data?.costTypes}
            projectId={modalState.data?.projectId}
            onSuccess={modalState.data?.onSuccess}
          />
        );
      case MODAL_TYPES.WINDOW_FORM:
        return (
          <WindowFormModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            window={modalState.data?.window}
            spaceId={modalState.data?.spaceId}
            space={modalState.data?.space}
          />
        );
      case MODAL_TYPES.DOCUMENTS_MODAL:
        return (
          <DocumentsModal
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            {...modalState.data.data}
          />
        );

      case MODAL_TYPES.VENDOR_FORM:
        return (
          <VendorFormModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            projectItem={modalState.data?.projectItem}
            vendorTypeId={modalState.data?.vendorTypeId}
            editVendorId={modalState.data?.editVendorId}
            isEditMode={!!modalState.data?.projectItem?.vendor}
          />
        );
      case MODAL_TYPES.PAYMENT_FORM:
        return (
          <PaymentFormModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            financialId={modalState.data?.financialId}
            onSubmit={modalState.data?.onSubmit}
          />
        );
      case MODAL_TYPES.BOQ_TEMPLATE_SELECTION:
        return (
          <BOQTemplateModal
            open={modalState.isOpen}
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            categoryId={modalState.data?.categoryId}
            templateId={modalState.data?.templateId}
            onSubmit={modalState.data?.onSubmit}
          />
        );
      case MODAL_TYPES.QUOTE_FORM:
        return (
          <QuoteFormModal
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            quote={modalState.data?.quote}
            projectId={modalState.data?.projectId}
            preselectedItems={modalState.data?.preselectedItems}
            preselectedItemTypes={modalState.data?.preselectedItemTypes}
            defaultVendorTypeId={modalState.data?.defaultVendorTypeId}
            initialMode={modalState.data?.initialMode}
          />
        );
      case MODAL_TYPES.TRADE_QUOTES:
        return (
          <TradeQuotesModal
            projectId={modalState.data?.projectId}
            tradeName={modalState.data?.tradeName}
            tradeVendorTypeId={modalState.data?.tradeVendorTypeId}
            projectItemIds={modalState.data?.projectItemIds}
          />
        );
      case MODAL_TYPES.QUOTE_COMPARISON:
        return (
          <QuoteComparisonModal
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            itemId={modalState.data?.itemId}
            quotes={modalState.data?.quotes}
            projectId={modalState.data?.projectId}
          />
        );
      case MODAL_TYPES.FLOOR_PLAN_ANALYSIS:
        return (
          <FloorPlanAnalysisWizard
            floorId={modalState.data?.floorId}
            floorName={modalState.data?.floorName}
            projectId={modalState.data?.projectId}
            onClose={closeModal}
            onComplete={modalState.data?.onComplete}
            existingFilePath={modalState.data?.existingFilePath}
            existingFileUrl={modalState.data?.existingFileUrl}
          />
        );
      case MODAL_TYPES.IMPORT_BOQ_NOTES:
        return (
          <ImportNotesModal
            onClose={closeModal}
            onSubmitRef={fn => (currentSubmitRef = fn)}
            projectId={modalState.data?.projectId}
            level0Chapters={modalState.data?.level0Chapters}
            existingNotes={modalState.data?.existingNotes}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={modalStyles.overlay}
      onClick={e => {
        // Only close if clicking the overlay itself, not modal content
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      {renderModal()}
    </div>
  );
}
